import React, { useState, useEffect } from 'react';
import { SensorConfigCard } from './SensorConfigCard';
import { REFRESH_INTERVALS } from '../../config';

/**
 * Sensor configuration panel component
 */
export const SensorConfigPanel = ({ isOpen, onClose, config, onSave }) => {
  const [localConfig, setLocalConfig] = useState(config);
  const [errors, setErrors] = useState({});
  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState(null);

  // Update local config when prop changes
  useEffect(() => {
    setLocalConfig(config);
  }, [config]);

  // Validate thresholds
  const validateThresholds = (type, values) => {
    const nums = [values.min, values.idealMin, values.idealMax, values.max];
    if (nums.some(n => n === '' || !Number.isFinite(Number(n)))) return 'Todos los valores deben ser números válidos';
    if (Number(values.min) >= Number(values.idealMin)) return 'Min debe ser menor que Min Ideal';
    if (Number(values.idealMin) >= Number(values.idealMax)) return 'Min Ideal debe ser menor que Max Ideal';
    if (Number(values.idealMax) >= Number(values.max)) return 'Max Ideal debe ser menor que Max';
    return null;
  };

  // Handle config change
  const handleChange = (type, field, value) => {
    const newConfig = {
      ...localConfig,
      [type]: {
        ...localConfig[type],
        [field]: value
      }
    };
    setLocalConfig(newConfig);

    // Validate
    const error = validateThresholds(type, newConfig[type]);
    setErrors(prev => ({ ...prev, [type]: error }));
  };

  // Handle save
  const handleSave = async () => {
    const hasErrors = Object.values(errors).some(e => e !== null);
    if (hasErrors) return;

    setSaving(true);
    setSaveError(null);
    try {
      await onSave(localConfig);
      onClose();
    } catch (e) {
      setSaveError('Error al guardar la configuración. Verifica que los valores sean válidos.');
    } finally {
      setSaving(false);
    }
  };

  const hasErrors = Object.values(errors).some(e => e !== null);

  return (
    <>
      <div className={`sidebar-overlay ${isOpen ? 'active' : ''}`} onClick={onClose} />
      <div className={`config-panel ${isOpen ? 'active' : ''}`}>
        <div className="config-header">
          <div className="config-title">
            <span>⚙️</span>
            Configuracion de Sensores
          </div>
          <button className="sidebar-close" onClick={onClose}>×</button>
        </div>

        <div className="config-content">
          <SensorConfigCard
            title="Temperatura (°C)"
            icon="🌡️"
            config={localConfig.temperature}
            onChange={(field, value) => handleChange('temperature', field, value)}
            error={errors.temperature}
          />

          <SensorConfigCard
            title="Oxigeno Disuelto (mg/L)"
            icon="💧"
            config={localConfig.oxygen}
            onChange={(field, value) => handleChange('oxygen', field, value)}
            error={errors.oxygen}
          />

          <SensorConfigCard
            title="Saturacion (%)"
            icon="📊"
            config={localConfig.saturation}
            onChange={(field, value) => handleChange('saturation', field, value)}
            error={errors.saturation}
          />

          {/* Refresh Interval */}
          <div className="config-card">
            <div className="config-card-header">
              <span className="config-card-icon">🔄</span>
              <span className="config-card-title">Intervalo de Actualizacion</span>
            </div>
            <div className="config-field">
              <label className="config-label">Actualizar dashboard cada</label>
              <select
                className="config-select"
                value={localConfig.refreshInterval || 1}
                onChange={(e) => setLocalConfig(prev => ({
                  ...prev,
                  refreshInterval: parseInt(e.target.value)
                }))}
              >
                {REFRESH_INTERVALS.map(interval => (
                  <option key={interval.value} value={interval.value}>
                    {interval.label}
                  </option>
                ))}
              </select>
            </div>
          </div>

          {saveError && <div className="config-save-error">{saveError}</div>}

          <button
            className="config-save-btn"
            onClick={handleSave}
            disabled={hasErrors || saving}
          >
            {saving ? 'Guardando…' : 'Guardar Configuracion'}
          </button>
        </div>
      </div>
    </>
  );
};
